import React, { Fragment, useContext } from 'react';
import { FlightSearch } from 'ts/flight/interfaces/FlightSearch';
import { useTranslation } from 'react-i18next';
import { dateFormat } from 'utils/shared/utils';
import ProposalHeart from 'components/shared/Proposal/Create/ProposalHeart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleArrowRight,
  faClock,
  faShoppingBag,
  faSuitcase,
} from '@fortawesome/free-solid-svg-icons';
import { getBounds, getElapsedStr } from 'utils/flight/flight';
import { getCurrency, getLogoUrl } from 'utils/utils';
import Button from 'components/ui/Button/Button';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { FlightsResultContext } from 'contexts/FlightsResultContext';
import { MainContext } from 'contexts/MainContext';
import { blobUrl } from 'constants/generic';

interface FlightPairsTicketProps {
  flight: FlightSearch;
  sessionStorageKey: string;
  soloMode?: boolean;
}

const FlightPairsTicket: React.FC<FlightPairsTicketProps> = ({
  flight,
  sessionStorageKey,
  soloMode,
}) => {
  const { t } = useTranslation();
  const bounds = getBounds(flight);
  const sfm = flight.flightSFM;
  const format = "dd/MM '<span class=\"text-bold\">'HH:mm'</span>'";
  const format2 = "'<span class=\"text-bold\">'HH:mm'</span>' dd/MM";
  const { proposal, setProposal } = useContext(MainContext);
  const flContext = useContext(FlightsResultContext);

  const onClickProposal = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();

    if (!proposal || !setProposal) return;
    const existingProposalIndex = proposal.flights.findIndex(
      (proposal) => proposal.item.id === flight.id
    );

    if (existingProposalIndex !== -1) {
      const updatedFlights = proposal.flights.filter((_, index) => index !== existingProposalIndex);
      setProposal({ ...proposal, flights: updatedFlights });
    } else {
      const newProposal = {
        item: JSON.parse(JSON.stringify(flight)),
        currency: flight.flighPrice.grossAndTaxWithMarkup.currency,
        price: flight.flighPrice.grossAndTaxWithMarkup.value,
        displayPrice: true,
        searchToken: sessionStorage.getItem(sessionStorageKey) || '',
      };

      setProposal({ ...proposal, flights: [...proposal.flights, newProposal] });
    }
  };

  const onClickContinue = () => {
    console.log(flight, 'flight', sessionStorageKey, flContext?.onClickChoose);
    flContext?.onClickChoose(flight, sessionStorageKey);
  };

  return (
    <div className="fl-pair-ticket position-relative">
      <ProposalHeart
        active={proposal?.flights.some((f) => f.item.id === flight.id) ?? false}
        onClickPropsal={onClickProposal}
      />
      <div className="fl-pair-ticket-summary flex-between">
        <span>
          {t('flight.base')}:{' '}
          <b>
            {getCurrency(sfm?.priceTax?.currency)}
            {Math.round(sfm?.priceNett?.value || 0)?.toLocaleString()}
          </b>
        </span>
        <span>
          {t('flight.tax')}:{' '}
          <b>
            {getCurrency(sfm?.priceTax?.currency)}
            {Math.round(sfm?.priceTax?.value || 0)?.toLocaleString()}
          </b>
        </span>
        <span>
          {t('flight.SFM')}:{' '}
          <b>
            {getCurrency(sfm?.priceProfit?.currency)}
            {Math.round(sfm?.priceProfit?.value || 0)?.toLocaleString()}
          </b>
        </span>
        <span>
          {t('flight.total_price')}:{' '}
          <b>
            {getCurrency(sfm?.price?.currency)}
            {Math.round(sfm?.price?.value || 0)?.toLocaleString()}
          </b>
        </span>
        {soloMode && (
          <Button
            className="btn btn-default btn-sm"
            title={t('hotel.book')}
            icon={faCircleArrowRight}
            iconclassname="mr-5 text-contrast"
            onClick={onClickContinue}
          />
        )}
      </div>

      {bounds.map((bound, index) => (
        <div className="fl-pair-ticket-body" key={index}>
          <div className="flex-between fl-pair-ticket-header-2">
            <div>
              <span
                dangerouslySetInnerHTML={{
                  __html: dateFormat(bound.departureDateTime, format),
                }}
              ></span>{' '}
              -
              <span
                dangerouslySetInnerHTML={{
                  __html: dateFormat(bound.arrivalDateTime, format2),
                }}
              ></span>
            </div>
            <div>
              <FontAwesomeIcon
                className={
                  (bound.listOfFlyBaggagePrice?.[0]?.pieces > 0 ? '' : 'color-danger') + ' mr-5'
                }
                icon={faSuitcase as any}
              />
              <span className="mr-5">{bound.listOfFlyBaggagePrice?.[0]?.pieces}</span>
              <FontAwesomeIcon icon={faShoppingBag as any} className="mr-5" />
              <span>{bound.listOfFlyBaggagePrice?.[0]?.maxWeightHandbag}</span>
            </div>
            <div>
              {t('filter.duration')}: <b>{getElapsedStr(t, bound.elapsedDTime)}</b>
            </div>
          </div>
          <div className="fl-pair-ticket-body-content">
            <div className="fl-pair-ticket-body-header flex-between">
              <span>{bound.airline.desc}</span>
              <span>{bound.cabin.desc}</span>
              <span>{flight?.fareFamilyInfo?.[0]?.desc}</span>
            </div>
            {bound.gdsFlyAirs.map((gds: any, index: number) => (
              <div key={index} className="fl-pair-ticket-item">
                <div className=" flex-between">
                  <div className="flex-column">
                    <span>
                      <span className="fl-pair-ticket-ico mr-5">{index + 1}</span>
                      <span className="text-bold text-contrast">
                        {gds.departureAirport.cityDesc} - {gds.arrivalAirport.cityDesc}
                      </span>
                    </span>

                    <span>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: dateFormat(gds.departureDateTime, format),
                        }}
                      ></span>{' '}
                      -
                      <span
                        dangerouslySetInnerHTML={{
                          __html: dateFormat(gds.arrivalDateTime, format2),
                        }}
                      ></span>
                    </span>
                    <span>
                      {gds.airline.desc}

                      {gds.operatingAirline.code !== gds.airline.code && (
                        <Tooltip
                          content={
                            t('flight.operated_by') +
                            ' ' +
                            (gds.operatingAirline.desc || gds.operatingAirline.code)
                          }
                          children={
                            <span
                              className="ml-5 fl-pair-ticket-ico"
                              style={{ background: 'var(--color-danger)' }}
                            >
                              {gds.operatingAirline.code}{' '}
                            </span>
                          }
                        />
                      )}
                    </span>
                  </div>
                  <div className="flex-column text-center">
                    <div className="text-bold" style={{ height: 23 }}>
                      {!!gds.connDTime && (
                        <Fragment>
                          <FontAwesomeIcon icon={faClock as any} className="mr-5" />
                          <span>{getElapsedStr(t, gds.connDTime)}</span>
                        </Fragment>
                      )}
                    </div>
                    <span
                      className="fl-pair-ticket-ico"
                      style={{ backgroundColor: 'var(--color-400)' }}
                    >
                      {gds.airline.code} {gds.flightNumber}
                    </span>
                    <span>{gds.aircraftType.code}</span>
                  </div>
                  <div className="flex-column">
                    <div style={{ height: 23 }}>
                      {!!gds.elapsedDTime && (
                        <span>
                          {t('filter.duration')}: <b>{getElapsedStr(t, gds.elapsedDTime)}</b>
                        </span>
                      )}
                    </div>
                    <span>
                      <span>{gds.departureAirport.code}</span> -
                      <span className="text-bold">{gds.arrivalAirport.code}</span>
                    </span>
                    <span>
                      <img
                        src={getLogoUrl(blobUrl, gds.airline.code)}
                        alt=""
                        style={{ maxWidth: 40, maxHeight: 25 }}
                      />
                    </span>
                  </div>
                </div>
                <div className="text-lh-sm text-bold mb-5">{bound.remark}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default FlightPairsTicket;
